import { styled } from '@mui/system';
import { SectionTitle } from "../../common/SectionTitle/SectionTitle";
import { DevExCell } from "./DevExCell";

import { List } from '../../common/List/List';
import { ListItem } from '../../common/List/ListItem';
import { DevExParagraph } from './DevExParagraph';
import flowsheet from '../../../assets/icons/flowsheet.svg';
import growth from '../../../assets/icons/growth.svg';
import rocketLaunch from '../../../assets/icons/rocket-launch.svg';
import backstageGreen from '../../../assets/icons/backstage-green.svg';
import devExLogo from '../../../assets/icons/devex.svg';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

const DevExContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '96px',
    paddingBottom: '96px',
    '@media (max-width: 1024px)': {
        paddingTop: '48px',
        paddingBottom: '48px',
        paddingLeft: '16px',
        paddingRight: '16px',
    }
});

const DevExTable = styled('table')({
    borderCollapse: 'collapse',
    width: '100%',
    '@media (max-width: 1024px)': {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    }
});

const TableCell = styled('td')(({ paddingTop }: { paddingTop?: string }) => ({
    width: '420px',
    paddingTop: paddingTop,
    '@media (max-width: 1024px)': {
        width: '100%',
        paddingTop: '0',
    }
}));

const TableRow = styled('tr')({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    height: '391px',

    '&:last-child': {
        height: '439px',
    },
    '@media (max-width: 1024px)': {
        flexDirection: 'column',
        height: 'auto',
        gap: '24px',

        '&:last-child': {
            height: 'auto',
        },
    }
});

const CellText = styled('p')({
    color: '#F1F7F7',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '18px',
    '@media (max-width: 1024px)': {
        fontSize: '12px',
        lineHeight: '16px',
    }
});

const GreenLink = styled('a')({
    color: '#00E378',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '18px',
    textDecorationLine: 'underline',
    textDecorationStyle: 'solid',
    '@media (max-width: 1024px)': {
        fontSize: '12px',
        lineHeight: '16px',
    }
});

const HorizontalDivider = styled('div')({
    width: '100%',
    height: '1px',
    backgroundColor: '#00644B',
    '@media (max-width: 1024px)': {
        display: 'none',
    }
});

const MobileHorizontalDivider = styled('div')({
    display: 'none',
    width: '100%',
    height: '1px',
    backgroundColor: '#00644B',
    marginBottom: '24px',
    '@media (max-width: 1024px)': {
        display: 'block',
    }
});
const VerticalDivider = styled('div')({
    width: '1px',
    height: '100%',
    backgroundColor: '#00644B',
    '@media (max-width: 1024px)': {
        display: 'none',
    }
});

export const DevEx = () => {
    const { t } = useTranslation();

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const developerListItems = [
        t('devEx.developerExperience.listItems.0'),
        t('devEx.developerExperience.listItems.1'),
        t('devEx.developerExperience.listItems.2'),
        t('devEx.developerExperience.listItems.3')
    ];

    const backstageListItems = [
        t('devEx.backstage.listItems.0'),
        t('devEx.backstage.listItems.1'),
        t('devEx.backstage.listItems.2'),
        t('devEx.backstage.listItems.3'),
        t('devEx.backstage.listItems.4'),
        t('devEx.backstage.listItems.5'),
        t('devEx.backstage.listItems.6')
    ];

    return (
        <DevExContainer>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
            >
                <SectionTitle title={t('devEx.title')} logo={devExLogo} marginBottom={isMobile ? '36px' : '55px'} />
            </motion.div>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                style={{ width: '100%', height: '100%' }}
            >
                <DevExTable>
                    <tbody>
                        <TableRow>
                            <TableCell>
                                <motion.div variants={itemVariants}>
                                    <DevExCell icon={rocketLaunch} title={t('devEx.developerExperience.title')}>
                                        <CellText>
                                            {t('devEx.developerExperience.description1')}
                                        </CellText>

                                        <CellText>
                                            {t('devEx.developerExperience.description2')}
                                        </CellText>

                                        <List marginLeft="24px">
                                            {developerListItems.map((item, index) => (
                                                <ListItem key={index} fontSize="14px">
                                                    {item}
                                                </ListItem>
                                            ))}
                                        </List>
                                    </DevExCell>
                                </motion.div>
                            </TableCell>
                            <MobileHorizontalDivider />
                            <VerticalDivider />
                            <TableCell>
                                <motion.div variants={itemVariants}>
                                    <DevExCell icon={backstageGreen} title={t('devEx.backstage.title')}>
                                        <CellText>
                                            <GreenLink href="https://backstage.io/" target="_blank">
                                                Backstage.io
                                            </GreenLink> {t('devEx.backstage.description')}
                                        </CellText>

                                        <List marginLeft="24px">
                                            {backstageListItems.map((item, index) => (
                                                <ListItem key={index} fontSize="14px">
                                                    {item}
                                                </ListItem>
                                            ))}
                                        </List>
                                    </DevExCell>
                                </motion.div>
                            </TableCell>
                        </TableRow>
                        <MobileHorizontalDivider />
                        <HorizontalDivider />
                        <TableRow>
                            <TableCell paddingTop="34px">
                                <motion.div variants={itemVariants}>
                                    <DevExCell icon={growth} title={t('devEx.growth.title')}>
                                        <DevExParagraph
                                            title={t('devEx.growth.emergingMarket.title')}
                                            text={t('devEx.growth.emergingMarket.text')}
                                        />
                                        <DevExParagraph
                                            title={t('devEx.growth.trend.title')}
                                            text={t('devEx.growth.trend.text')}
                                        />
                                        <DevExParagraph
                                            title={t('devEx.growth.enterprise.title')}
                                            text={t('devEx.growth.enterprise.text')}
                                        />
                                    </DevExCell>
                                </motion.div>
                            </TableCell>
                            <MobileHorizontalDivider />
                            <VerticalDivider />
                            <TableCell paddingTop="34px">
                                <motion.div variants={itemVariants}>
                                    <DevExCell icon={flowsheet} title={t('devEx.keyDrivers.title')}>
                                        <DevExParagraph
                                            title={t('devEx.keyDrivers.simplification.title')}
                                            text={t('devEx.keyDrivers.simplification.text')}
                                        />
                                        <DevExParagraph
                                            title={t('devEx.keyDrivers.devops.title')}
                                            text={t('devEx.keyDrivers.devops.text')}
                                        />
                                        <DevExParagraph
                                            title={t('devEx.keyDrivers.objective.title')}
                                            text={t('devEx.keyDrivers.objective.text')}
                                        />
                                    </DevExCell>
                                </motion.div>
                            </TableCell>
                        </TableRow>
                    </tbody>
                </DevExTable>
            </motion.div>
        </DevExContainer>
    );
};
